import { FsCalculationsService } from './fs-calculations.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { FinancialStatementBackendService } from './financial-statement-backend.service';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { FinancialStatementPart } from 'src/app/domain/financial-statement/models/common/FinancialStatementPart';
import { GeneralLedgerTransaction } from '../../models/common/GeneralLedgerTransaction';
import { FinancialStatementAccount } from '../../models/common/FinancialStatementAccount';
import { FinancialStatementItem } from '../../models/common/FinancialStatementItem';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { ExcelGeneralLedgerDTO } from '../../models/DTOs/gl-upload/ExcelGeneralLedgerDTO';
import { FinancialStatementDTO } from '../../models/DTOs/common/FinancialStatementDTO';
import { GeneralLedgerTransactionDTO } from '../../models/DTOs/common/GeneralLedgerTransactionDTO';
import { FinancialStatementItemDTO } from '../../models/DTOs/common/FinancialStatementItemDTO';
import { chunkSizeKB, ChunkUploadService } from '../chunk-upload/chunk-upload.service';
import { InvestorCapitalAccountDTO } from '../../models/DTOs/capital-account/InvestorCapitalAccountDTO';
import { InvestorCapitalAccount } from '../../models/capital-account/InvestorCapitalAccount';
import { ManagementEntity } from '../../../organization/dto/ManagementEntity';
import { Fund } from '../../../fund-performance/dto/Fund';
import { FsSums } from '../../utils/common/FsSums';
import { BALANCE_SHEET_PARTS, OwnershipEntityType, PROFIT_LOSS_PARTS } from '../../utils/common/utilityClasses';
import { FsGettersSettersService } from './fs-getters-setters.service';

@Injectable({
  providedIn: 'root',
})
export class FinancialStatementService {
  private _manualEntries = new BehaviorSubject<GeneralLedgerTransaction[]>([]);
  private _ownershipEntities = new BehaviorSubject<(Fund | ManagementEntity)[]>([]);

  selectedPeriod: Date;
  ownershipUuid: string; // uuid of selected fund or mngmnt entity from navbar
  ownershipName: string; // name of selected fund or mngmnt entity
  ownershipEntityType: OwnershipEntityType; // if ownership entity is Fund or mngmnt entity
  financialStatementYear: number;

  constructor(
    private financialStatementBackend: FinancialStatementBackendService,
    private notificationsService: NotificationsService,
    private chunkUploadService: ChunkUploadService,
    private fsGetterSetter: FsGettersSettersService,
  ) { }

  get financialStatement(): FinancialStatement {
    return this.fsGetterSetter.financialStatement.value;
  }

  get manualEntries(): BehaviorSubject<GeneralLedgerTransaction[]> {
    return this._manualEntries;
  }

  get ownershipEntities(): BehaviorSubject<(Fund | ManagementEntity)[]> {
    return this._ownershipEntities;
  }

  setOwnershipEntity(ownershipEntity: Fund | ManagementEntity): void {
    if (!ownershipEntity || ownershipEntity.uuid === this.ownershipUuid) {
      return;
    }
    this.ownershipUuid = ownershipEntity.uuid;
    this.ownershipName = ownershipEntity.name;
    this.ownershipEntityType = 'fundId' in ownershipEntity ? OwnershipEntityType.FUND : OwnershipEntityType.MANAGEMENT_ENTITY;
  }

  setSelectedPeriod(period: Date): void {
    this.selectedPeriod = period;
  }

  /** Updates fs and all other relevant data that is managed through this service; i.e. manual entries/accounts when added.
   * @param isFsUpdate denotes if the actual fs object or its data was expected to be changed as opposed to a plain fetch.
   * This would typically be true only for Fund/Entity or Period change or entry/account creation
  */
  async updateFinancialStatementData(isFsUpdate: boolean): Promise<void> {
    const fs = await this.loadFinancialStatement();
    if (!fs) {
      this.fsGetterSetter.setFinancialStatement(null);
      this.clearData();
      return;
    }
    if (fs.uuid === this.financialStatement?.uuid && !isFsUpdate) {
      // this skips data update in the case that update was triggered by navigation change but object is unchanged, i.e. oninit, both entity and period start as null - are changed - trigger fs load - but we only want one fs
      return;
    }
    this.fsGetterSetter.setFinancialStatement(fs);
    this.manualEntries.next(fs.gl.filter((transaction) => transaction.isManualEntry === true));
  }

  // saves gl and loads built financial statement
  async saveGLDataFromUpload(dataForFinancialStatement: ExcelGeneralLedgerDTO[]): Promise<void> {
    await this.saveGLData(dataForFinancialStatement);
    const fs = await this.loadFinancialStatement(false);
    this.fsGetterSetter.setFinancialStatement(fs);
  }

  // finds and returns glData for selectedPeriod, else outputs error
  async getGl(withManualEntries: boolean): Promise<GeneralLedgerTransaction[]> {
    if (this.ownershipUuid && this.selectedPeriod) {
      const glData = await firstValueFrom(this.financialStatementBackend.getGl(
        this.ownershipUuid, this.ownershipEntityType, this.selectedPeriod, withManualEntries));
      if (glData) {
        return glData;
      }
    }
  }

  // finds and returns last saved glData for fund, returns empty array if none found
  async getGLForEntityWithoutPeriod(): Promise<GeneralLedgerTransaction[]> {
    if (!this.ownershipUuid) {
      return;
    }
    return firstValueFrom(this.financialStatementBackend.getGlForEntityWithoutPeriod(this.ownershipUuid, this.ownershipEntityType));
  }

  clearData(): void {
    this.fsGetterSetter.setFinancialStatement(null);
    this.manualEntries.next([]);
  }

  async deleteFsData(): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    await firstValueFrom(this.financialStatementBackend.deleteFs(this.financialStatement.uuid));
    this.clearData();
    this.notificationsService.success(NOTIFICATIONS.DELETE_SUCCESS);
  }

  async getFinancialStatement(showSpinner: boolean): Promise<FinancialStatementDTO> {
    if (this.ownershipUuid && this.selectedPeriod) {
      const bsData =
        await firstValueFrom(this.financialStatementBackend
          .getFinancialStatement(this.ownershipUuid, this.ownershipEntityType, this.selectedPeriod, showSpinner));
      if (bsData?.gl.length) {
        return bsData;
      }
    }
  }

  async getFinancialStatementByPeriod(period: Date, showSpinner: boolean): Promise<FinancialStatement> {
    if (this.ownershipUuid && period) {
      const bsData =
        await firstValueFrom(this.financialStatementBackend
          .getFinancialStatement(this.ownershipUuid, this.ownershipEntityType, period, showSpinner));
      if (bsData?.gl.length) {
        const compiledFinancialStatement = this.buildFinancialStatement(bsData);
        this.setAccountItems(compiledFinancialStatement.tb, compiledFinancialStatement.items);
        return compiledFinancialStatement;
      }
    }
  }

  async loadFinancialStatement(showSpinner = true): Promise<FinancialStatement> {
    const loadedFinancialStatement = await this.getFinancialStatement(showSpinner);
    if (loadedFinancialStatement) {
      const compiledFinancialStatement = this.buildFinancialStatement(loadedFinancialStatement);
      this.setAccountItems(compiledFinancialStatement.tb, compiledFinancialStatement.items);
      return compiledFinancialStatement;
    }
  }

  // compile BS data into saved structure
  buildFinancialStatement(loadedFinancialStatement: FinancialStatementDTO): FinancialStatement {
    if (loadedFinancialStatement) {
      return this.convertDTOsToObjects(loadedFinancialStatement);
    }
  }

  convertDTOsToObjects(financialStatementDTO: FinancialStatementDTO): FinancialStatement {
    const accounts: FinancialStatementAccount[] = [];
    const items: FinancialStatementItem[] = [];
    const transactions: GeneralLedgerTransaction[] = [];
    const parts: FinancialStatementPart[] = [];
    const investors: InvestorCapitalAccount[] = [];
    const compiledFinancialStatement = new FinancialStatement(
      [],
      financialStatementDTO.uuid,
      financialStatementDTO.period,
      financialStatementDTO.ownershipUuid,
      financialStatementDTO.lastGlUpload?.replace(/T/g, ' ').slice(0, -3),
      financialStatementDTO.isCompleted,
      [],
      [],
      [],
    );

    financialStatementDTO.gl.forEach((t) => {
      transactions.push(
        new GeneralLedgerTransaction(
          t.id,
          t.transactionDate,
          t.journalEntryCode,
          new FinancialStatementAccount(t.accountId),
          new FinancialStatementAccount(t.contraAccountId),
          t.description,
          t.comment,
          t.debit,
          t.credit,
          t.isManualEntry,
          t.isMarkedForConversion,
        ),
      );
    });

    financialStatementDTO.tb.forEach((accountDTO) => {
      accounts.push(
        new FinancialStatementAccount(
          accountDTO.id,
          accountDTO.code,
          accountDTO.name,
          accountDTO.isManual,
          transactions.filter((transaction) => accountDTO.glTransactionsIds?.includes(transaction.id)),
          [],
          false,
          new FsSums(),
        ),
      );
    });

    financialStatementDTO.items.forEach((itemDTO) => {
      items.push(new FinancialStatementItem(
        itemDTO.id,
        itemDTO.name,
        accounts.filter((account) => itemDTO.accountsIds?.includes(account.id)),
        itemDTO.assetsAndLiabilities,
        itemDTO.partName,
        itemDTO.orderInTable,
        new FsSums(),
      ));
    });

    financialStatementDTO.investors.forEach((investorDTO: InvestorCapitalAccountDTO) => {
      investors.push(
        new InvestorCapitalAccount(investorDTO.uuid, investorDTO.code, investorDTO.name, investorDTO.isGP, investorDTO.commitment),
      );
    });

    transactions.forEach((transaction) => {
      transaction.account = accounts.find((account) => account.id === transaction.account.id);
      transaction.contraAccount = accounts.find((account) => account.id === transaction.contraAccount.id);
    });

    items.forEach((item: FinancialStatementItem) => {
      const itemPart = parts.find((part) => part.partName === item.partName);
      if (itemPart) {
        itemPart.financialStatementItems.push(item);
      } else {
        parts.push(new FinancialStatementPart(item.partName, [item], new FsSums()));
      }
    });
    parts.forEach((part) => part.sortItemsByOrder());

    compiledFinancialStatement.balanceSheetParts = parts
      .filter((part) => BALANCE_SHEET_PARTS.includes(part.partName));
    compiledFinancialStatement.profitLossParts = parts
      .filter((part) => PROFIT_LOSS_PARTS.includes(part.partName));

    compiledFinancialStatement.balanceSheetNetTotals = new FsSums();
    compiledFinancialStatement.profitLossNetTotals = new FsSums();
    compiledFinancialStatement.items = items;
    compiledFinancialStatement.tb = accounts.sort((a1, a2) => a1.code.localeCompare(a2.code));
    compiledFinancialStatement.gl = transactions;
    compiledFinancialStatement.investors = investors;
    FsCalculationsService.calculateAccountsTotals(compiledFinancialStatement.tb, this.financialStatementYear);
    compiledFinancialStatement.setQuarterlyGl();
    FsCalculationsService.calculateBalanceSheetTotals(
      compiledFinancialStatement.balanceSheetNetTotals, compiledFinancialStatement.balanceSheetParts, false,
    );
    FsCalculationsService.calculateBalanceSheetTotals(
      compiledFinancialStatement.profitLossNetTotals, compiledFinancialStatement.profitLossParts, true,
    );
    // for each account, set items equal to those items who contain some account with a matching id
    compiledFinancialStatement.tb.forEach((account) =>
      account.financialStatementItems = compiledFinancialStatement.items.filter((item) =>
        item.financialStatementAccounts.some((tbAccount) => tbAccount.id === account.id)));

    compiledFinancialStatement.items.forEach((item) => item.findSums());
    return compiledFinancialStatement;
  }

  /**
    For each in input account array, set items using following filter:
      filter items where the item.accounts contains the account and,
      is either not asset and liabilities, or if it is asset and liabilities,
      if it is assets, its sum should be positive; if it is liabilities, its sum is negative;
      with the same conditions applied to profits and losses relatively
  */
  setAccountItems(accounts: FinancialStatementAccount[], items: FinancialStatementItem[]): void {
    accounts.forEach((account) =>
      account.financialStatementItems = items.filter((item) => item.financialStatementAccounts.includes(account) &&
        (!item.assetsAndLiabilities || (item.partName === 'Assets' && item.sums.sumITP >= 0) ||
          (item.partName === 'Liabilities' && item.sums.sumITP < 0) || (item.partName === 'Profits' && item.sums.sumITP >= 0) ||
          (item.partName === 'Losses' && item.sums.sumITP < 0))));
  }

  updateItem(item: FinancialStatementItem, notify: boolean = true): void {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    const itemDTO = item?.toDTO();
    try {
      void firstValueFrom(this.financialStatementBackend.updateItem(itemDTO));
    } catch (e) {
      return;
    }
    if (notify) {
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    }
  }

  async saveNewItem(item: FinancialStatementItem): Promise<FinancialStatementItem> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    const savedItem = await firstValueFrom(this.financialStatementBackend.saveItem(item.toDTO(), this.financialStatement.uuid));
    if (savedItem) {
      const savedItemDTO = new FinancialStatementItemDTO(savedItem.id, savedItem.name, savedItem.orderInTable,
        savedItem.assetsAndLiabilities, savedItem.partName, savedItem.accountsIds);
      item = savedItemDTO.toEntity(this.financialStatement.tb);
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
      return item;
    }
  }

  async deleteItem(item: FinancialStatementItem): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    const deleted = await firstValueFrom(this.financialStatementBackend.deleteItem(item.id));
    if (deleted) {
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    }
  }

  async saveManualEntry(manualEntry: GeneralLedgerTransaction): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    // convert to DTO and save
    // using copy constructor because on compile, manualEntry is seen as plain object with no .toDTO method
    const manualEntryDTO: GeneralLedgerTransactionDTO = GeneralLedgerTransaction.toDTO(manualEntry);
    const savedManualEntriesDTOs = await firstValueFrom(this.financialStatementBackend
      .saveManualEntry(manualEntryDTO, this.financialStatement.uuid));
    // remove from manualEntries old entries if saving from edit
    const existingManualEntries = this.manualEntries.value.filter((entry) =>
      !savedManualEntriesDTOs.some((savedEntry) => savedEntry.id === entry.id));
    const newManualEntries = [];
    // convert new entries to entities
    savedManualEntriesDTOs?.forEach((entryDTO: GeneralLedgerTransactionDTO) => {
      const entryAccount = this.financialStatement.tb.find((account) => account.id === entryDTO.accountId);
      const entryContraAccount = this.financialStatement.tb.find((contraAccount) => contraAccount.id === entryDTO.contraAccountId);
      const entry = new GeneralLedgerTransaction(
        entryDTO.id, entryDTO.transactionDate, entryDTO.journalEntryCode, entryAccount, entryContraAccount,
        entryDTO.description, entryDTO.comment, entryDTO.debit, entryDTO.credit, entryDTO.isManualEntry, entryDTO.isMarkedForConversion,
      );
      // remove old entry from account
      entryAccount.glTransactions = entryAccount.glTransactions.filter((accountTransaction) => entry.id !== accountTransaction.id);
      // add entry to account and gl
      entryAccount.glTransactions.push(entry);
      // remove old entry from gl
      this.financialStatement.gl = this.financialStatement.gl.filter((accountTransaction) =>
        entry.id !== accountTransaction.id);
      // add to gl
      this.financialStatement.gl.push(entry);
      newManualEntries.push(entry);
    });
    // add new entries to manualEntries and propagate
    this.manualEntries.next(existingManualEntries.concat(newManualEntries));
    FsCalculationsService.calculateAccountsTotals(this.financialStatement.tb, this.financialStatementYear);
    FsCalculationsService.calculatePartsBalances(this.financialStatement.balanceSheetParts);
    FsCalculationsService.calculatePartsBalances(this.financialStatement.profitLossParts);
    this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
  }

  async saveManualEntries(manualEntries: GeneralLedgerTransaction[]): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    try {
      const manualEntriesDTO = manualEntries.map((entry) => GeneralLedgerTransaction.toDTO(entry));
      const savedEntriesDTOs = await firstValueFrom(this.financialStatementBackend.saveManualEntries(
        manualEntriesDTO,
        this.financialStatement.uuid,
      ));
      this.handleSavedManualEntries(savedEntriesDTOs);
      FsCalculationsService.calculateAccountsTotals(this.financialStatement.tb, this.financialStatementYear);
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.SAVE_UNSUCCESSFUL);
    }
  }

  async deleteManualEntries(manualEntries: GeneralLedgerTransaction[]): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }

    const manualEntryIdsForDelete = manualEntries.map((manualEntry) => manualEntry.id);

    try {
      await firstValueFrom(this.financialStatementBackend.deleteManualEntries(manualEntryIdsForDelete));
    } catch (error) {
      this.notificationsService.error({ title: 'Cannot delete manual entry', message: error.message, timeout: 7000 });
      return;
    }

    this.manualEntries.next(this.manualEntries.value.filter((entry) => !manualEntryIdsForDelete.includes(entry.id)));
    this.updateAccountsAfterTransactionsDeletion(manualEntries);
    this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
  }

  async convertManualEntry(newJournalEntryCode: string, oldJournalEntryCode: string): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    const convertedEntriesDTOs = await firstValueFrom(this.financialStatementBackend.convertManualEntries(
      this.financialStatement.gl.find((transaction) => transaction.journalEntryCode === oldJournalEntryCode).id, newJournalEntryCode));
    convertedEntriesDTOs?.forEach((entryDTO) => {
      const entryAccount = this.financialStatement.tb.find((account) => account.id === entryDTO.accountId);
      const transaction = entryAccount.glTransactions.find((glTransaction) => glTransaction.journalEntryCode === oldJournalEntryCode);
      transaction.journalEntryCode = newJournalEntryCode;
      transaction.isManualEntry = false;
      FsCalculationsService.calculateAccountsTotals([transaction.account], this.financialStatementYear);
    });
    this.manualEntries.next(this.manualEntries.value.filter((entry) => entry.journalEntryCode !== oldJournalEntryCode));
  }

  async saveManualAccount(newAccount: FinancialStatementAccount, isUpdate: boolean): Promise<FinancialStatementAccount> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    const savedAccountDTO =
      await firstValueFrom(this.financialStatementBackend.saveManualAccount(newAccount.toDTO(), this.financialStatement.uuid));
    const savedAccount = new FinancialStatementAccount(savedAccountDTO.id, savedAccountDTO.code, savedAccountDTO.name,
      true, [], [], false, new FsSums());
    if (!isUpdate) {
      this.financialStatement.tb.push(savedAccount);
    }
    return savedAccount;
  }

  deleteManualAccounts(accounts: FinancialStatementAccount[]): Promise<number[]> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    try {
      return firstValueFrom(this.financialStatementBackend.deleteManualAccounts(accounts.map((account) => account.id)));
    } catch (error) {
      this.notificationsService.error({ title: 'Cannot delete manual account', message: error.message, timeout: 7000 });
    }
  }

  calculateAccountTotals(accounts: FinancialStatementAccount[], startDate?: Date, endDate?: Date): void {
    FsCalculationsService.calculateAccountsTotals(accounts, this.financialStatementYear, startDate, endDate);
  }

  // returns true if the financial statement isCompleted
  checkLockStatusOfFinancialStatement(): boolean {
    if (this.financialStatement?.isCompleted) {
      this.notificationsService.error(NOTIFICATIONS.FS_IS_LOCKED_ERROR);
      return true;
    }
    return false;
  }

  async clearFsStructure(): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    await firstValueFrom(this.financialStatementBackend.clearFsStructure(this.financialStatement.uuid));
    await this.updateFinancialStatementData(true);
  }

  async copyFsStructure(period: Date): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement()) {
      return;
    }
    await firstValueFrom(this.financialStatementBackend.copyFsStructure(this.financialStatement.uuid, period));
    await this.updateFinancialStatementData(true);
  }

  private handleSavedManualEntries(savedEntriesDTOs: GeneralLedgerTransactionDTO[]): void {
    const existingManualEntries = this.manualEntries.value.filter(
      (entry) => !savedEntriesDTOs.some((savedEntry) => savedEntry.id === entry.id),
    );
    const newManualEntries = savedEntriesDTOs.map((entryDTO) => this.createAndProcessEntry(entryDTO));
    this.manualEntries.next(existingManualEntries.concat(newManualEntries));
  }

  private createAndProcessEntry(entryDTO: GeneralLedgerTransactionDTO): GeneralLedgerTransaction {
    const entryAccount = this.financialStatement.tb.find((account) => account.id === entryDTO.accountId);
    const entryContraAccount = this.financialStatement.tb.find((account) => account.id === entryDTO.contraAccountId);

    const entry = new GeneralLedgerTransaction(
      entryDTO.id,
      entryDTO.transactionDate,
      entryDTO.journalEntryCode,
      entryAccount,
      entryContraAccount,
      entryDTO.description,
      entryDTO.comment,
      entryDTO.debit,
      entryDTO.credit,
      entryDTO.isManualEntry,
      entryDTO.isMarkedForConversion,
    );

    this.removeOldEntryFromAccount(entryAccount, entry.id);
    this.removeOldEntryFromGL(entry.id);

    entryAccount.glTransactions.push(entry);
    this.financialStatement.gl.push(entry);

    return entry;
  }

  private removeOldEntryFromAccount(account: FinancialStatementAccount, entryId: number): void {
    account.glTransactions = account.glTransactions.filter((transaction) => transaction.id !== entryId);
  }

  private removeOldEntryFromGL(entryId: number): void {
    this.financialStatement.gl = this.financialStatement.gl.filter((transaction) => transaction.id !== entryId);
  }

  private updateAccountsAfterTransactionsDeletion(manualEntries: GeneralLedgerTransaction[]): void {
    const affectedAccounts = [];
    manualEntries.forEach((entry) => {
      const account = this.financialStatement.tb.find((a) => a.code === entry.account.code);
      account.glTransactions = account.glTransactions.filter((t) => t.id !== entry.id);
      affectedAccounts.push(account);
    });

    FsCalculationsService.calculateAccountsTotals(affectedAccounts, this.financialStatementYear);
  }


  private async saveGLData(glData: ExcelGeneralLedgerDTO[]): Promise<void> {
    if (this.checkLockStatusOfFinancialStatement() || !glData.length) {
      return;
    }
    try {
      const dataSizeKB = JSON.stringify(glData).length / 1024;
      if (dataSizeKB > chunkSizeKB) {
        await this.saveChunkedGL(glData);
      } else {
        await this.saveSingleGL(glData);
      }
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.GL_UPLOAD_ERROR);
    }
  }

  private async saveChunkedGL(glData: ExcelGeneralLedgerDTO[]): Promise<void> {
    const [sessionUUID, totalNumberOfChunks] = await this.chunkUploadService.sendChunkedData(glData);
    return firstValueFrom(this.financialStatementBackend.saveChunkedGL(
      sessionUUID,
      totalNumberOfChunks,
      this.ownershipUuid,
      this.ownershipEntityType,
      this.selectedPeriod,
    ));
  }

  private async saveSingleGL(glData: ExcelGeneralLedgerDTO[]): Promise<void> {
    return firstValueFrom(this.financialStatementBackend.saveGL(
      glData,
      this.ownershipUuid,
      this.ownershipEntityType,
      this.selectedPeriod,
    ));
  }
}

